import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

export function Card({
  className,
  children
}: {
  className?: string
  children: ReactNode
}): ReactNode {
  return (
    <section className={cn('rounded-lg border border-line bg-surface', className)}>
      {children}
    </section>
  )
}

export function CardHeader({
  title,
  description,
  icon: Icon,
  actions
}: {
  title: string
  description?: string
  icon?: LucideIcon
  actions?: ReactNode
}): ReactNode {
  // shrink-0 so the header keeps its height inside a height-capped card.
  return (
    <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2">
      <div className="flex min-w-0 items-center gap-3">
        {Icon && <Icon size={20} className="shrink-0 text-accent" aria-hidden />}
        <div className="min-w-0">
          <h2 className="label-caps truncate text-fg">{title}</h2>
          {description && <p className="truncate mt-[-1px] text-[11.5px] text-muted">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  )
}

/** A titled block inside the dealer panel — quieter than a full card. */
export function Section({
  title,
  icon: Icon,
  actions,
  children
}: {
  title: string
  icon?: LucideIcon
  actions?: ReactNode
  children: ReactNode
}): ReactNode {
  return (
    <Card>
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h3 className="label-caps flex items-center gap-2 text-muted">
          {Icon && <Icon size={15} className="text-accent" aria-hidden />}
          {title}
        </h3>
        {actions}
      </header>
      <div className="p-4">{children}</div>
    </Card>
  )
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action
}: {
  icon: LucideIcon
  title: string
  description?: string
  action?: ReactNode
}): ReactNode {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <span className="grid h-11 w-11 place-items-center rounded-full border border-line bg-raised text-subtle">
        <Icon size={18} aria-hidden />
      </span>
      <p className="text-[13px] font-semibold text-fg">{title}</p>
      {description && <p className="max-w-sm text-[12px] text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function Spinner({ label }: { label?: string }): ReactNode {
  return (
    <div className="flex items-center justify-center gap-2 px-6 py-14 text-muted">
      <Loader2 size={16} className="animate-spin text-accent" aria-hidden />
      <span className="label-caps">{label ?? 'Loading'}</span>
    </div>
  )
}

/** Key/value line used throughout the detail panel. */
export function DataRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-[11.5px] text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-[12.5px] font-medium text-fg">{children}</dd>
    </div>
  )
}
